import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import {
  BaseOneResponseInterface,
  BulkResponseDataInterface,
} from '../../../../shared/model/interface/crud-response-interface.model';
import * as oeeAppReducer from '../../../oee.reducer';
import * as AppActions from '../../../app/actions';
import { ErrorMessageService } from '../../../../shared/service/error-message.service';
import { IBurstSetting } from '../../../../view/settings/api-automation-settings/burst-settings/burst-settings.model';
import { BurstSettingService } from './burst-settings.service';
import {
  ActionTypes,
  BurstSettingsLoading,
  BurstSettingsLoaded,
  FetchError,
  GetBurstSetting,
  GetBurstSettingCompleted,
  EditBurstSetting,
  EditBurstSettingCompleted,
  AddBurstSetting,
  AddBurstSettingCompleted,
  DeleteBurstSettings,
  DeleteBurstSettingsCompleted,
  SiteDataCompleted,
  LineDataLoading,
  LineDataCompleted,
  AllLinesDataCompleted,
} from './burst-settings.actions';
import { LineService } from '../../../../shared/service/line/line.service';
import { SiteService } from '../../../../shared/service/filter/site.service';
import { StatusesEnum } from '../../../../../constants';

@Injectable()
export class BurstSettingEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: BurstSettingService,
    private readonly store: Store<oeeAppReducer.OeeAppState>,
    private readonly errorMessageService: ErrorMessageService,
    private readonly siteService: SiteService,
    private readonly lineService: LineService,
  ) {}

  getDataForDatatable = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.BURST_SETTINGS_DATA_LOADING),
      switchMap((payload: BurstSettingsLoading) => {
        const andConditions = [];
        const searchText: string = payload.params.searchText?.trim() ?? '';
        let httpParams: HttpParams = new HttpParams()
          .append('sort', 'id,DESC')
          .append('limit', String(payload.params.perPage || 1000))
          .append('page', String(payload.params.page));

        if (payload.params.siteIds?.length) {
          andConditions.push({ siteId: { $in: payload.params.siteIds } });
        }

        if (payload.params.lineIds?.length) {
          andConditions.push({ lineId: { $in: payload.params.lineIds } });
        }

        if (searchText) {
          andConditions.push({
            $or: [{ 'site.name': { $cont: searchText } }, { 'line.title': { $cont: searchText } }],
          });
        }

        if (andConditions.length > 0) {
          httpParams = httpParams.set('s', JSON.stringify({ $and: andConditions }));
        }

        if (payload.params.orderBy && payload.params.orderDesc !== undefined) {
          httpParams = httpParams.set('sort', `${payload.params.orderBy},${payload.params.orderDesc}`);
        }

        return this.service.getData(httpParams).pipe(
          switchMap((response) => of(new BurstSettingsLoaded(response))),
          catchError((error) => of(new FetchError(error))),
        );
      }),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.GET_BURST_SETTING),
      switchMap((objectData: GetBurstSetting) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.getOne(objectData.recordId).pipe(
          switchMap((response: BaseOneResponseInterface<IBurstSetting>) =>
            of(new GetBurstSettingCompleted(response), new AppActions.HideLoader()),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  addOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.ADD_BURST_SETTING),
      switchMap((objectData: AddBurstSetting) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.addOne(objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<IBurstSetting>) =>
            of(
              new AddBurstSettingCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  editOne = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.EDIT_BURST_SETTING),
      switchMap((objectData: EditBurstSetting) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.editOne(objectData.recordId, objectData.record).pipe(
          switchMap((response: BaseOneResponseInterface<IBurstSetting>) =>
            of(
              new EditBurstSettingCompleted(response),
              ...(objectData.hideLoaderAfterSuccess ? [new AppActions.HideLoader()] : []),
            ),
          ),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  deleteOnes = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.DELETE_BURST_SETTINGS),
      switchMap((objectData: DeleteBurstSettings) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.service.deleteOnes(objectData.payload).pipe(
          switchMap((response: BulkResponseDataInterface) => {
            this.errorMessageService.getTranslatedErrorMessage(response.data);

            return of(
              new DeleteBurstSettingsCompleted(objectData.payload.length > 1, response),
              new AppActions.HideLoader(),
            );
          }),
          catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
        );
      }),
      catchError((error) => of(new FetchError(error), new AppActions.HideLoader())),
    ),
  );

  getSiteData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.SITES_DATA_LOADING),
      switchMap(() =>
        from(this.siteService.getData(new HttpParams().set('limit', '1000'))).pipe(
          switchMap((response) => of(new SiteDataCompleted(response))),
          catchError((error) => of(new FetchError(error))),
        ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getLineData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.LINES_DATA_LOADING),
      switchMap((objectData: LineDataLoading) =>
        this.lineService
          .getLines(
            new HttpParams()
              .set('s', JSON.stringify({ statusId: StatusesEnum.ACTIVE, siteId: objectData.siteId }))
              .set('limit', '1000')
              .set('join', 'lineTypeName'),
            true,
          )
          .pipe(
            switchMap((response) => of(new LineDataCompleted(response))),
            catchError((error) => of(new FetchError(error))),
          ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );

  getAllLinesData = createEffect(() =>
    this.actions$.pipe(
      ofType(ActionTypes.ALL_LINES_DATA_LOADING),
      switchMap(() =>
        this.lineService
          .getLines(
            new HttpParams().set('s', JSON.stringify({ statusId: StatusesEnum.ACTIVE })).set('limit', '1000'),
            true,
          )
          .pipe(
            switchMap((response) => of(new AllLinesDataCompleted(response))),
            catchError((error) => of(new FetchError(error))),
          ),
      ),
      catchError((error) => of(new FetchError(error))),
    ),
  );
}
